import React, { useState, useEffect } from 'react';
import { Send, Loader2, CheckCircle2, Mail, Building2, Search, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { LegalModal } from './Footer';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIContactForm = () => {
  const { lang, t } = useLang();
  const [phase, setPhase] = useState('intro'); // intro | company | chat
  const [conversation, setConversation] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ai-contact/status`)
      .then((res) => setAiAvailable(res.data.available))
      .catch(() => setAiAvailable(true));
  }, []);

  const apiErrorMessage = (error) => {
    if (error.response?.status === 503) return t('form.err503');
    if (error.response?.status === 429) return t('form.err429');
    return t('form.errGeneric');
  };

  const terms = useContent('legal_terms', { title: 'AGB', sections: [] });
  const privacy = useContent('legal_privacy', { title: 'Datenschutzerklärung', sections: [] });

  const consentParts = (() => {
    const [pre, rest] = t('form.consent').split('{terms}');
    const [mid, post] = (rest || '').split('{privacy}');
    return { pre, mid, post };
  })();

  const startConversation = async (info) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = await axios.post(`${API}/ai-contact/next-question`, {
        conversation_history: [],
        current_answer: null,
        company_info: info || null,
        language: lang
      });
      setCurrentQuestion(response.data.question);
      setPhase('chat');
      setLoading(false);
    } catch (error) {
      console.error('Error starting conversation:', error);
      setErrorMsg(apiErrorMessage(error));
      setLoading(false);
    }
  };

  const handleCompanyLookup = async () => {
    if (!companyName.trim() && !websiteUrl.trim()) {
      await startConversation(null);
      return;
    }
    setAnalyzing(true);
    setErrorMsg('');
    let info = null;
    try {
      const response = await axios.post(`${API}/ai-contact/company-lookup`, {
        company_name: companyName.trim(),
        website_url: websiteUrl.trim()
      });
      info = response.data;
      setCompanyInfo(info);
    } catch (error) {
      console.error('Company lookup failed:', error);
      info = companyName.trim() ? { company_name: companyName.trim(), website_url: websiteUrl.trim() || null } : null;
      setCompanyInfo(info);
    }
    setAnalyzing(false);
    await startConversation(info);
  };

  const handleSkipCompany = async () => {
    setCompanyInfo(null);
    await startConversation(null);
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();

    if (!userAnswer.trim()) return;

    setLoading(true);
    setErrorMsg('');

    const newConversation = [
      ...conversation,
      {
        question: currentQuestion,
        answer: userAnswer
      }
    ];

    setConversation(newConversation);

    try {
      const response = await axios.post(`${API}/ai-contact/next-question`, {
        conversation_history: newConversation,
        current_answer: userAnswer,
        company_info: companyInfo,
        language: lang
      });

      if (response.data.isComplete) {
        await axios.post(`${API}/ai-contact/submit`, {
          conversation_history: newConversation,
          terms_accepted: true,
          company_info: companyInfo,
          language: lang
        });

        setIsComplete(true);
        setCurrentQuestion(response.data.question);
      } else {
        setCurrentQuestion(response.data.question);
      }

      setUserAnswer('');
      setLoading(false);
    } catch (error) {
      console.error('Error submitting answer:', error);
      setConversation(conversation);
      setErrorMsg(apiErrorMessage(error));
      setLoading(false);
    }
  };

  if (!aiAvailable) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl p-12 shadow-lg" data-testid="contact-form-disabled">
          <div className="w-16 h-16 bg-[#F5F5F7] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#0071E3]" strokeWidth={1.5} />
          </div>
          <h3 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
            {t('form.unavailableTitle')}
          </h3>
          <p className="text-[#86868B] text-lg mb-6">
            {t('form.unavailableText')}
          </p>
          <div className="space-y-2">
            <a href="mailto:info@invictaflow.ch" className="block text-[#0071E3] hover:underline text-lg font-medium">
              info@invictaflow.ch
            </a>
            <a href="tel:+41796969899" className="block text-[#0071E3] hover:underline text-lg font-medium">
              +41 79 696 98 99
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl p-12 shadow-lg">
          <div className="mb-8">
            <div className="w-16 h-16 bg-[#0071E3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8 text-[#0071E3]" strokeWidth={1.5} />
            </div>
            <h3 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
              {t('form.startTitle')}
            </h3>
            <p className="text-[#86868B] text-lg mb-8">
              {t('form.startText')}
            </p>
          </div>

          <label className="flex items-start gap-3 text-left mb-6 cursor-pointer select-none max-w-md mx-auto">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#0071E3] flex-shrink-0"
              data-testid="contact-consent-checkbox"
            />
            <span className="text-sm text-[#86868B]">
              {consentParts.pre}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                className="text-[#0071E3] hover:underline"
                data-testid="contact-consent-terms-link"
              >
                {t('form.termsLabel')}
              </button>
              {consentParts.mid}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
                className="text-[#0071E3] hover:underline"
                data-testid="contact-consent-privacy-link"
              >
                {t('form.privacyLabel')}
              </button>
              {consentParts.post}
            </span>
          </label>

          <Button
            size="lg"
            onClick={() => { setErrorMsg(''); setPhase('company'); }}
            disabled={!consentChecked}
            className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full px-8 h-12 disabled:opacity-50"
            data-testid="contact-start-button"
          >
            {t('form.start')}
            <Send className="ml-2 w-5 h-5" />
          </Button>

          {errorMsg && (
            <p className="text-sm text-red-600 mt-4" data-testid="contact-error-message">
              {errorMsg}
            </p>
          )}

          {!consentChecked && (
            <p className="text-xs text-[#86868B] mt-4">
              {t('form.consentHint')}
            </p>
          )}
        </div>

        {showTerms && <LegalModal data={terms} onClose={() => setShowTerms(false)} testId="contact-terms-modal" />}
        {showPrivacy && <LegalModal data={privacy} onClose={() => setShowPrivacy(false)} testId="contact-privacy-modal" />}
      </div>
    );
  }

  if (phase === 'company') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl p-12 shadow-lg text-center" data-testid="contact-company-step">
          {analyzing || loading ? (
            <div className="py-8">
              <div className="w-16 h-16 bg-[#0071E3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-[#0071E3] animate-spin" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                {analyzing ? t('form.analyzingTitle') : t('form.momentTitle')}
              </h3>
              <p className="text-[#86868B]">
                {analyzing ? t('form.analyzingText') : t('form.momentText')}
              </p>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 bg-[#0071E3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-[#0071E3]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                {t('form.companyTitle')}
              </h3>
              <p className="text-[#86868B] mb-8">
                {t('form.companyText')}
              </p>

              <div className="space-y-4 text-left max-w-md mx-auto mb-6">
                <div>
                  <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">{t('form.companyNameLabel')}</label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t('form.companyNamePh')}
                    className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#1D1D1F] font-medium block mb-1.5">
                    {t('form.websiteLabel')} <span className="text-[#86868B] font-normal">{t('form.optional')}</span>
                  </label>
                  <Input
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder={t('form.websitePh')}
                    className="bg-[#F5F5F7] border-0 rounded-xl h-12"
                    data-testid="company-website-input"
                  />
                </div>
              </div>

              {errorMsg && (
                <p className="text-sm text-red-600 mb-4" data-testid="contact-error-message">
                  {errorMsg}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  onClick={handleCompanyLookup}
                  disabled={!companyName.trim() && !websiteUrl.trim()}
                  className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full px-8 h-12 disabled:opacity-50"
                  data-testid="company-continue-button"
                >
                  <Search className="mr-2 w-5 h-5" />
                  {t('form.analyzeBtn')}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleSkipCompany}
                  className="border-[#D2D2D7] text-[#1D1D1F] hover:bg-[#F5F5F7] rounded-full px-8 h-12"
                  data-testid="company-skip-button"
                >
                  <User className="mr-2 w-5 h-5" />
                  {t('form.skipBtn')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl p-12 shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
            {t('form.thanksTitle')}
          </h3>
          <p className="text-[#86868B] text-lg">
            {currentQuestion}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-3xl p-8 shadow-lg">
        {companyInfo?.zefix?.name && (
          <div className="bg-[#0071E3]/5 rounded-2xl px-4 py-3 mb-6 flex items-center gap-3" data-testid="company-found-badge">
            <Building2 className="w-5 h-5 text-[#0071E3] flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#1D1D1F]">
              <span className="font-medium">{companyInfo.zefix.name}</span>
              {companyInfo.zefix.legal_seat && <span className="text-[#86868B]"> · {companyInfo.zefix.legal_seat}</span>}
              <span className="text-[#86868B]"> · {t('form.registryFound')}</span>
            </p>
          </div>
        )}

        {/* Conversation History */}
        <div className="mb-6 space-y-4 max-h-96 overflow-y-auto">
          {conversation.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="bg-[#F5F5F7] rounded-2xl p-4">
                <p className="text-sm text-[#86868B] mb-1">{t('form.questionLabel')} {index + 1}</p>
                <p className="text-[#1D1D1F]">{item.question}</p>
              </div>
              <div className="bg-[#0071E3]/5 rounded-2xl p-4 ml-8">
                <p className="text-sm text-[#0071E3] mb-1">{t('form.yourAnswer')}</p>
                <p className="text-[#1D1D1F]">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Current Question */}
        <div className="bg-[#F5F5F7] rounded-2xl p-6 mb-6">
          <p className="text-sm text-[#86868B] mb-2">
            {t('form.questionLabel')} {conversation.length + 1}
          </p>
          <p className="text-lg text-[#1D1D1F] font-medium">
            {currentQuestion}
          </p>
        </div>

        {/* Answer Input */}
        <form onSubmit={handleSubmitAnswer} className="space-y-4">
          {errorMsg && (
            <p className="text-sm text-red-600" data-testid="contact-error-message">
              {errorMsg}
            </p>
          )}
          <Textarea
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder={t('form.answerPh')}
            rows={4}
            disabled={loading}
            className="bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl resize-none focus:ring-1 focus:ring-[#0071E3]"
          />

          <Button
            type="submit"
            disabled={loading || !userAnswer.trim()}
            className="w-full bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12 group"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('form.processing')}
              </>
            ) : (
              <>
                {t('form.next')}
                <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </form>

        {/* Progress Indicator */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#86868B]">
            {t('form.step').replace('{n}', conversation.length + 1)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIContactForm;
